import {
  GetOrderQuery,
  type IGetOrderQueryResponse,
} from '@fhs-legacy/frontend/src/remote/queries/order';
import { client as legacyClient } from '@fhs-legacy/frontend/src/state/graphql/client';
import logger from '@fhs-legacy/frontend/src/utils/logger';
import { Network } from '@fhs-legacy/frontend/src/utils/network/status';
import { IServerOrder } from '@rbi-ctg/menu';

import { ORDER_SUBMITTED_STATUSES } from '../order-status';

export const RECOVERY_WINDOW_MS = 90000;
const RETRY_DELAY_MS = 3000;
const QUERY_TIMEOUT_MS = 5000;

type SubmittedOrderLookup =
  | { status: 'submitted'; order: IServerOrder }
  | { status: 'notSubmitted' }
  | { status: 'unavailable' };

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

async function queryOrderStatus(rbiOrderId: string, timeoutMs: number) {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error('Order status lookup timed out')), timeoutMs);
  });

  try {
    return await Promise.race([
      legacyClient.query<IGetOrderQueryResponse>({
        query: GetOrderQuery,
        variables: { rbiOrderId },
        fetchPolicy: 'network-only',
      }),
      timeoutPromise,
    ]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
}

export async function recoverSubmittedOrder(rbiOrderId: string): Promise<SubmittedOrderLookup> {
  // Wall-clock deadline so Order Status Unknown appears after 90s even while offline.
  const deadline = Date.now() + RECOVERY_WINDOW_MS;
  let lastKnownOrder: IServerOrder | null = null;

  while (Date.now() < deadline) {
    const remainingMs = deadline - Date.now();
    if (remainingMs <= 0) {
      break;
    }

    const networkStatus = await Network.getStatus();
    if (!networkStatus.connected) {
      const waitMs = Math.min(RETRY_DELAY_MS, remainingMs);
      if (waitMs > 0) {
        await delay(waitMs);
      }
      continue;
    }

    try {
      const response = await queryOrderStatus(
        rbiOrderId,
        Math.min(QUERY_TIMEOUT_MS, remainingMs)
      );

      const order = response.data?.order;
      if (order?.status) {
        lastKnownOrder = order;
        if (ORDER_SUBMITTED_STATUSES.has(order.status)) {
          return { status: 'submitted', order };
        }
        // Pre-commit statuses (PRICE_SUCCESSFUL, PAYMENT_ERROR, etc.) can be stale from a
        // prior attempt or from a response the client never received. Keep polling until the
        // recovery window expires so the confirming UX stays visible on slow/offline networks.
      }
      // Null/missing can be short-lived after a successful commit (read propagation).
      // Keep polling; deadline yields unavailable so we do not allow a duplicate retry.
    } catch (error) {
      logger.warn({
        message: '[RecoverSubmittedOrder] Order status lookup failed, still retrying',
        rbiOrderId,
        error: error instanceof Error ? error.message : String(error),
      });
    }

    const waitMs = Math.min(RETRY_DELAY_MS, deadline - Date.now());
    if (waitMs > 0) {
      await delay(waitMs);
    }
  }

  if (lastKnownOrder?.status) {
    return { status: 'notSubmitted' };
  }

  return { status: 'unavailable' };
}

/** Single GetOrder check — use after clearing a stale offline failure before showing an error. */
export async function lookupSubmittedOrderOnce(
  rbiOrderId: string
): Promise<SubmittedOrderLookup> {
  try {
    const networkStatus = await Network.getStatus();
    if (!networkStatus.connected) {
      return { status: 'unavailable' };
    }

    const response = await queryOrderStatus(rbiOrderId, QUERY_TIMEOUT_MS);
    const order = response.data?.order;
    if (order?.status && ORDER_SUBMITTED_STATUSES.has(order.status)) {
      return { status: 'submitted', order };
    }
    if (order?.status) {
      return { status: 'notSubmitted' };
    }
    return { status: 'unavailable' };
  } catch (error) {
    logger.warn({
      message: '[RecoverSubmittedOrder] One-shot order status lookup failed',
      rbiOrderId,
      error: error instanceof Error ? error.message : String(error),
    });
    return { status: 'unavailable' };
  }
}
